/**
 * TanStack Query hook for loading KYB form list via tRPC.
 * Uses server-side pagination and filtering instead of fetching all pages.
 */

import { transformDBKYBToKYCFormData } from "../services/data/api/kycProvider";
import { trpc } from "../trpc";
import type { KYCFormData } from "../types";

type KybListRow = Parameters<typeof transformDBKYBToKYCFormData>[0];

export function useKYBFormList(params: {
	enabled: boolean;
	status?: string;
	search?: string;
	page?: number;
	limit?: number;
}) {
	const { enabled, status, search, page = 1, limit = 50 } = params;
	const trimmedSearch = search?.trim();
	const input = {
		page,
		limit,
		...(status && status !== "all" ? { status } : {}),
		...(trimmedSearch ? { search: trimmedSearch } : {}),
	};

	const query = trpc.kyb.list.useQuery(input, {
		enabled,
		staleTime: 60_000,
		refetchOnMount: true,
		refetchOnWindowFocus: false,
	});

	const forms: KYCFormData[] =
		query.data?.data?.map((row: KybListRow) =>
			transformDBKYBToKYCFormData(row),
		) ?? [];

	return {
		data: forms,
		total: query.data?.pagination?.total ?? 0,
		totalPages: query.data?.pagination?.totalPages ?? 0,
		page: query.data?.pagination?.page ?? page,
		isLoading: query.isLoading,
		error: query.error,
		refetch: query.refetch,
	};
}
